/* ── Formatters ── */
import { getCurrencySymbol } from "../../../utils/currency";

const locale = typeof navigator !== "undefined" ? navigator.language : "en-US";

export const fmtNumber = (v, d = 2) =>
  v === null || v === undefined || isNaN(Number(v))
    ? "—"
    : Number(v).toLocaleString(locale, { minimumFractionDigits: d, maximumFractionDigits: d });

export const fmtPercent = (v, d = 2) =>
  v === null || v === undefined || isNaN(Number(v))
    ? "—"
    : `${Number(v) >= 0 ? "+" : ""}${fmtNumber(v, d)}%`;
/** Color helper: green for positive, red for negative, muted for null/zero */

export const fmtCurrency = (v, d = 2, compact = false) => {
  if (v === null || v === undefined || isNaN(Number(v))) return "—";
  const sym = getCurrencySymbol();
  const num = Number(v);
  if (compact) {
    const abs = Math.abs(num);
    const sign = num < 0 ? "-" : "";
    if (abs >= 1e12) return `${sign}${sym}${fmtNumber(abs / 1e12, d)}T`;
    if (abs >= 1e9) return `${sign}${sym}${fmtNumber(abs / 1e9, d)}B`;
    if (abs >= 1e6) return `${sign}${sym}${fmtNumber(abs / 1e6, d)}M`;
    if (abs >= 1e3) return `${sign}${sym}${fmtNumber(abs / 1e3, d)}K`;
    return `${sign}${sym}${fmtNumber(abs, d)}`;
  }
  return `${sym}${fmtNumber(v, d)}`;
};

/** Format a gain/loss value with explicit +/- prefix for accessibility (3.1.1) */
export const fmtGain = (v, d = 2) => {
  if (v === null || v === undefined || isNaN(Number(v))) return "—";
  const n = Number(v);
  const sign = n >= 0 ? "+" : "";
  return `${sign}${fmtNumber(v, d)}`;
};

export const gainColor = (v) => {
  if (v === null || v === undefined) return "var(--color-text-secondary)";
  const n = Number(v);
  if (isNaN(n)) return "var(--color-text-secondary)";
  if (n > 0) return "var(--color-success)";
  if (n < 0) return "var(--color-error)";
  return "var(--color-text-secondary)";
};

/* ── Signal chip config ── */
export const SIGNAL_STYLES = {
  "Accumulate": { bg: "#0ca30c", text: "#0b0b0b" },
  "Watch (FV uncertain)": { bg: "#fab219", text: "#0b0b0b" },
  "Trim (FV uncertain)": { bg: "#fab219", text: "#0b0b0b" },
  "Trim": { bg: "#ec835a", text: "#0b0b0b" },
  "Avoid (value-trap risk)": { bg: "#d03b3b", text: "#ffffff" },
  "Hold": { bg: "#52514e", text: "#ffffff" },
  "N/A (no earnings anchor)": { bg: "#c3c2b7", text: "#0b0b0b" },
  "N/A (data quality issue)": { bg: "#0b0b0b", text: "#ffffff" },
};

export const SIGNAL_TOOLTIPS = {
  "Accumulate": "At/below entry, passes quality, anchors agree, not in a technical freefall.",
  "Watch (FV uncertain)": "At/below entry and passes quality, but the anchors disagree by more than 1.5x, so the fair value itself isn't trustworthy.",
  "Trim": "At/above exit, technically overbought, and fails quality.",
  "Trim (FV uncertain)": "Same as Trim, but the anchors disagree — don't trust the fair value here either.",
  "Avoid (value-trap risk)": "At/below entry, but fails quality.",
  "Hold": "Everything else (the majority of names at any given time).",
  "N/A (no earnings anchor)": "No fair-value anchor available at all.",
  "N/A (data quality issue)": "This name's price is known-corrupted in the source data; every number shown for it is unreliable, not just the fair value.",
};

/**
 * Tooltip text for the FV Analyst value.
 * Prefers num_target_analysts_3m, falls back to num_target_analysts_12m.
 * Returns null (no tooltip) if neither is available.
 */
export const fvAnalystTooltip = (fvAnalyst, n3m, n12m) => {
  if (n3m == null && n12m == null) return null;
  const sym = getCurrencySymbol();
  const priceTxt = fvAnalyst != null ? `${sym}${fmtNumber(fvAnalyst)}` : "—";
  const n = n3m != null ? Number(n3m) : Number(n12m);
  const period = n3m != null ? "last 3 months" : "last 12 months";
  return `Analyst target ${priceTxt} — based on ${n.toLocaleString()} analyst${n === 1 ? "" : "s"} (${period})`;
};

export const signalStyle = (s) =>
  SIGNAL_STYLES[s] ?? { bg: "var(--color-surface-hover)", text: "var(--color-text-secondary)" };

/** Color helper for IV confidence levels (HIGH / MEDIUM / LOW) */
export const confidenceColor = (c) => {
  const v = String(c || "").toUpperCase();
  if (v === "HIGH") return "var(--color-success)";
  if (v === "MEDIUM") return "var(--color-warning)";
  if (v === "LOW") return "var(--color-error)";
  return "var(--color-text-muted)";
};

/** Format a date string (e.g. "2026-05-05") to the user locale, or "—" */
export const fmtDate = (d) => {
  if (!d) return "—";
  const dt = new Date(d);
  return isNaN(dt.getTime()) ? "—" : dt.toLocaleDateString();
};

export const gradeColor = (g) => {
  if (!g || g === "N/A" || g === "—") return "var(--color-text-muted)";
  const l = g.charAt(0).toUpperCase();
  if (l === "A") return "#15803d";
  if (l === "B") return "#84CC16";
  if (l === "C") return "#eab308";
  if (l === "D") return "#ef4444";
  return "#dc2626";
};

/* ── Constants ── */
export const COLORS = [
  "#3b82f6", "#10b981", "#f59e0b", "#ef4444", "#8b5cf6",
  "#ec4899", "#06b6d4", "#84cc16", "#f97316", "#6366f1",
];

export const RATING_CATEGORIES = [
  { key: "Strong Buy", color: "#15803d" },
  { key: "Buy", color: "#84CC16" },
  { key: "Hold", color: "#eab308" },
  { key: "Sell", color: "#ef4444" },
  { key: "Strong Sell", color: "#dc2626" },
  { key: "Not Covered", color: "#9ca3af" },
];